import { apiClient } from "./client";
import type {
  BatchCreateSummary,
  CreateUserRequest,
  CreateUserResult,
  ExportUserItemDto,
  KiroUserListItem,
  ListUsersFilters,
  ListUsersResultDto,
  RecommendUsersResultDto,
} from "./types";

/** §3.2 单条建号 */
export function createUser(req: CreateUserRequest) {
  return apiClient.post<CreateUserResult>("/users", req);
}

/** §3.2 批量建号 */
export function batchCreateUsers(users: CreateUserRequest[]) {
  return apiClient.post<BatchCreateSummary>("/users/batch", { users });
}

/**
 * §8.1 用户列表(不含密码字段),支持分页 + 筛选(用户名/邮箱快速查找,
 * 层级/订阅状态/激活状态/创建时间区间/到期时间区间筛选)。
 *
 * 不传 filters 时行为等价于旧版调用(仅按 accountId 查询,默认第 1 页、
 * 每页 20 条)——CreateUserPage 的"自动生成用户信息"需要拿到某账号下的
 * 全部用户名做去重,因此单独传 pageSize 拉大页(§3.2),不经过筛选条件。
 */
export function listUsers(accountId: string, filters: ListUsersFilters = {}) {
  return apiClient.get<ListUsersResultDto>("/users", {
    account_id: accountId,
    username: filters.username,
    email: filters.email,
    tier: filters.tier,
    subscription_status: filters.subscriptionStatus,
    activation_status: filters.activationStatus,
    created_from: filters.createdFrom,
    created_to: filters.createdTo,
    expiry_from: filters.expiryFrom,
    expiry_to: filters.expiryTo,
    in_use: filters.inUse,
    page: filters.page,
    page_size: filters.pageSize,
  });
}

/** §6.3 按需查看密码明文(仅 Admin) */
export function getPassword(accountId: string, email: string) {
  return apiClient.get<{ email: string; password: string }>(
    `/users/${encodeURIComponent(email)}/password`,
    { account_id: accountId }
  );
}

/** §7.1 退订 */
export function unsubscribeUser(accountId: string, email: string) {
  return apiClient.post<{
    account_id: string;
    email: string;
    subscription_status: string;
    already_unsubscribed: boolean;
    needs_manual: boolean;
  }>(`/users/${encodeURIComponent(email)}/unsubscribe`, { account_id: accountId });
}

/** 启用退订用户:重新加入原分层组,订阅状态从 CANCELED 改回 PENDING */
export function reactivateUser(accountId: string, email: string) {
  return apiClient.post<{
    account_id: string;
    email: string;
    subscription_status: string;
  }>(`/users/${encodeURIComponent(email)}/reactivate`, { account_id: accountId });
}

/** §7.1/§7.2 升降级 */
export function changeTier(accountId: string, email: string, newTier: string) {
  return apiClient.post<{
    account_id: string;
    email: string;
    old_tier: string;
    new_tier: string;
    direction: string;
    subscription_status: string;
    same_tier: boolean;
    needs_manual: boolean;
  }>(`/users/${encodeURIComponent(email)}/change-tier`, {
    account_id: accountId,
    new_tier: newTier,
  });
}

/** 手动调整到期日(用户列表页"到期日"选择年月后调用) */
export function updateSubscriptionEnd(accountId: string, email: string, subscriptionEndAt: string) {
  return apiClient.put<{ account_id: string; email: string; subscription_end_at: string }>(
    `/users/${encodeURIComponent(email)}/subscription-end`,
    { account_id: accountId, subscription_end_at: subscriptionEndAt }
  );
}

/**
 * 导出用户名单(含密码,仅 Admin),支持按创建日期区间/邮箱/用户名/层级筛选。
 * 需求⑥新增:订阅状态/激活状态/到期日区间/使用中筛选。
 */
export function exportUsers(params: {
  accountId: string;
  dateFrom?: string;
  dateTo?: string;
  email?: string;
  username?: string;
  tier?: string;
  subscriptionStatus?: string;
  activationStatus?: string;
  expiryFrom?: string;
  expiryTo?: string;
  inUse?: string;
}) {
  return apiClient.get<{ users: ExportUserItemDto[]; count: number }>("/users/export", {
    account_id: params.accountId,
    date_from: params.dateFrom,
    date_to: params.dateTo,
    email: params.email,
    username: params.username,
    tier: params.tier,
    subscription_status: params.subscriptionStatus,
    activation_status: params.activationStatus,
    expiry_from: params.expiryFrom,
    expiry_to: params.expiryTo,
    in_use: params.inUse,
  });
}

/** 跨模块推荐候选查询(Kiro-Go Fleet Manager 创建/充值页面使用) */
export function recommendUsers(tier: string) {
  return apiClient.get<RecommendUsersResultDto>("/users/recommend", { tier });
}

/** 手动/程序化标记用户"是否在使用中" */
export function setInUse(accountId: string, email: string, inUse: boolean) {
  return apiClient.put<{ account_id: string; email: string; in_use: boolean }>(
    `/users/${encodeURIComponent(email)}/in-use`,
    { account_id: accountId, in_use: inUse }
  );
}

/**
 * §7.6(v2.22)手动删除已退订用户,取代此前每日自动扫描删除。
 * 后端逐一校验(必须仍是 CANCELED + 实时核验无任何组成员关系)后执行删除,
 * 删除成功的记录会同时从 IdC 与台账(用户列表)中移除。
 */
export function batchDeleteCanceledUsers(targets: { account_id: string; email: string }[]) {
  return apiClient.post<{
    total_candidates: number;
    deleted: number;
    skipped: number;
    failed: number;
    results: { account_id: string; email: string; outcome: string; detail?: string }[];
  }>("/users/batch-delete-canceled", { targets });
}

/** 通过 Playwright 自动化为指定用户在 kiro.dev 创建一个 Kiro API Key(ksk_)
 *
 * 采用异步架构（API Gateway 29s 硬限制，Playwright 需要 60-180s）：
 * 1. POST 立即返回 task_id（PENDING）
 * 2. 轮询 GET 直到 SUCCESS / NEEDS_MANUAL / FAILED
 * 最终返回和原来同款的 {email, ksk_key, status, key_name} 响应。
 */
export async function createKiroApiKey(
  email: string,
  accountId: string,
  keyName?: string
): Promise<{
  email: string;
  ksk_key: string | null;
  status: "SUCCESS" | "NEEDS_MANUAL" | "FAILED";
  key_name: string;
  error_detail?: string;
}> {
  // Step 1: 提交任务，立即拿 task_id
  const submitted = await apiClient.post<{
    task_id: string;
    status: string;
    email: string;
  }>(`/users/${encodeURIComponent(email)}/create-kiro-api-key`, {
    account_id: accountId,
    key_name: keyName,
  });

  const { task_id } = submitted;

  // Step 2: 轮询，最多 180s（每 4s 一次，共 45 次）
  const MAX_POLLS = 45;
  const POLL_INTERVAL_MS = 4000;

  for (let i = 0; i < MAX_POLLS; i++) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    const result = await apiClient.get<{
      status: string;
      task_id: string;
      email: string;
      ksk_key?: string | null;
      key_name?: string;
      error_detail?: string;
    }>(`/users/${encodeURIComponent(email)}/kiro-api-key-task/${encodeURIComponent(task_id)}`);

    if (result.status === "PENDING") continue;

    return {
      email: result.email,
      ksk_key: result.ksk_key ?? null,
      status: result.status as "SUCCESS" | "NEEDS_MANUAL" | "FAILED",
      key_name: result.key_name ?? keyName ?? email,
      error_detail: result.error_detail,
    };
  }

  // 超时（轮询耗尽）
  return {
    email,
    ksk_key: null,
    status: "NEEDS_MANUAL",
    key_name: keyName ?? email,
    error_detail: "自动创建超时(>180s)，请稍后重试或手动录入 Key",
  };
}

/** Kiro-Go Fleet 创建/充值成功后,按邮箱把所用 Kiro 用户在 kiro_users 台账里
 *  同步为 in_use=true 并回写 subscription_end_at(到期日)。email-based,
 *  两种录入模式(推荐用户/手动录入)都适用,未命中的邮箱记入 not_found 不报错。 */
export function markFleetConsumed(entries: { email: string; expires_at: string }[]) {
  return apiClient.post<{
    updated_count: number;
    updated: { account_id: string; email: string; in_use: boolean; subscription_end_at?: string }[];
    not_found: string[];
  }>("/users/mark-fleet-consumed", { entries });
}

/** Fleet 邮箱+到期日 → kiro_users.subscription_end_at 同步(两步确认,confirm=false 只预览) */
export function fleetSyncSubscriptionEnd(confirm: boolean) {
  return apiClient.post<{
    dry_run: boolean;
    total_fleet_emails: number;
    updated_count: number;
    updated: { email: string; before: string | null; after: string; fleet_instance_ids: string[] }[];
    unchanged_count: number;
    fleet_only_count: number;
    fleet_only: { email: string; proposed_expiry: string }[];
  }>("/consistency/fleet-sync-subscription-end", { confirm });
}
